'use client';

import { useEffect, useRef, useState, useCallback, useId, type HTMLAttributes } from 'react';
import { MapPin, Search, Loader2, X, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input/input';
import { Button } from '@/components/ui/button/button';
import { cn } from '@/lib/utils';
import { useGooglePlacesApiKey } from '@/lib/queries/settings';

const DEFAULT_PLACE_TYPES: string[] = ['geocode', 'establishment'];
const SEARCH_DEBOUNCE_MS = 300;

export interface PlaceAddressComponent {
  longName: string;
  shortName: string;
  types: string[];
}

export interface PlaceLocation {
  placeId: string;
  formattedAddress: string;
  coordinates: { lat: number; lng: number };
  name?: string;
  locality?: string;
  country?: string;
  addressComponents?: PlaceAddressComponent[];
}

interface Prediction {
  placeId: string;
  mainText: string;
  secondaryText: string;
  fullText: string;
}

interface GooglePlacesAutocompleteProps {
  value?: string;
  initialValue?: string;
  onInputChange?: (value: string) => void;
  onSelect: (place: PlaceLocation) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  apiKey?: string;
  types?: string[];
  countryCode?: string;
  searchContext?: string;
  showSearchIcon?: boolean;
  showConfigurationErrors?: boolean;
  dropdownClassName?: string;
  autoComplete?: string;
  autoCapitalize?: string;
  enterKeyHint?: HTMLAttributes<HTMLInputElement>['enterKeyHint'];
}

// ---------------------------------------------------------------------------
// Script loading
// ---------------------------------------------------------------------------

async function loadPlacesApi(apiKey: string): Promise<void> {
  if (window.google?.maps?.places) return;

  const existing = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');

  if (!existing) {
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Maps API'));
      document.head.appendChild(script);
    });
  } else if (!window.google?.maps?.places) {
    await new Promise<void>((resolve, reject) => {
      if (window.google?.maps?.places) return resolve();
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Script load failed')));
    });
  }

  if (!window.google?.maps?.places) {
    throw new Error('Google Places library failed to initialize');
  }
}

function hasNewApi(): boolean {
  return (
    typeof google.maps.places.AutocompleteSuggestion?.fetchAutocompleteSuggestions === 'function'
  );
}

// ---------------------------------------------------------------------------
// Autocomplete predictions — new API with legacy fallback
// ---------------------------------------------------------------------------

// Legacy Autocomplete shorthand types that are NOT valid in the new Places API.
// The new API's `includedPrimaryTypes` only accepts Table A types.
// When these are encountered we omit the filter so the API returns all results.
const LEGACY_ONLY_TYPES = new Set(['address', 'geocode', 'establishment', '(regions)', '(cities)']);

function mapTypesForNewApi(types?: string[]): string[] | undefined {
  if (!types?.length) return undefined;
  const mapped = types.filter((t) => !LEGACY_ONLY_TYPES.has(t));
  return mapped.length > 0 ? mapped : undefined;
}

function mapPredictions(suggestions: google.maps.places.AutocompleteSuggestion[]): Prediction[] {
  return suggestions
    .filter(
      (s): s is typeof s & { placePrediction: NonNullable<typeof s.placePrediction> } =>
        !!s.placePrediction,
    )
    .map((s) => ({
      placeId: s.placePrediction.placeId,
      mainText: s.placePrediction.mainText?.text ?? '',
      secondaryText: s.placePrediction.secondaryText?.text ?? '',
      fullText: s.placePrediction.text.text,
    }));
}

async function fetchSuggestionsNew(
  input: string,
  sessionToken: google.maps.places.AutocompleteSessionToken,
  types?: string[],
  regionCodes?: string[],
): Promise<Prediction[]> {
  const baseRequest = {
    input,
    sessionToken,
    includedPrimaryTypes: mapTypesForNewApi(types),
  };

  try {
    const { suggestions } =
      await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        ...baseRequest,
        includedRegionCodes: regionCodes,
      });
    return mapPredictions(suggestions);
  } catch (err) {
    if (!regionCodes?.length) throw err;
    // Invalid region code (e.g. 'xe') — retry without region filter
    const { suggestions } =
      await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(baseRequest);
    return mapPredictions(suggestions);
  }
}

function fetchSuggestionsLegacy(
  input: string,
  sessionToken: google.maps.places.AutocompleteSessionToken,
  types?: string[],
  countryCode?: string,
): Promise<Prediction[]> {
  const service = new google.maps.places.AutocompleteService();
  return new Promise((resolve) => {
    service.getPlacePredictions(
      {
        input,
        sessionToken,
        types,
        componentRestrictions: countryCode ? { country: countryCode } : undefined,
      },
      (results, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !results) {
          resolve([]);
          return;
        }
        resolve(
          results.map((r) => ({
            placeId: r.place_id,
            mainText: r.structured_formatting.main_text,
            secondaryText: r.structured_formatting.secondary_text,
            fullText: r.description,
          })),
        );
      },
    );
  });
}

// ---------------------------------------------------------------------------
// Place details — new API with legacy fallback
// ---------------------------------------------------------------------------

function parseComponents(
  components: Array<{ longText?: string | null; shortText?: string | null; types: string[] }>,
) {
  let locality: string | undefined;
  let country: string | undefined;
  const mapped = components.map((c) => {
    if (c.types.includes('locality')) locality = c.longText ?? undefined;
    if (c.types.includes('country')) country = c.shortText ?? undefined;
    return { longName: c.longText ?? '', shortName: c.shortText ?? '', types: c.types };
  });
  return { locality, country, mapped };
}

async function fetchPlaceDetailsNew(placeId: string): Promise<PlaceLocation> {
  const place = new google.maps.places.Place({ id: placeId });
  await place.fetchFields({
    fields: ['displayName', 'formattedAddress', 'location', 'addressComponents'],
  });

  const { locality, country, mapped } = place.addressComponents
    ? parseComponents(place.addressComponents)
    : { locality: undefined, country: undefined, mapped: undefined };

  return {
    placeId: place.id ?? placeId,
    formattedAddress: place.formattedAddress ?? '',
    coordinates: { lat: place.location?.lat() ?? 0, lng: place.location?.lng() ?? 0 },
    name: place.displayName ?? undefined,
    locality,
    country,
    addressComponents: mapped,
  };
}

let legacyAttrDiv: HTMLDivElement | null = null;

function fetchPlaceDetailsLegacy(placeId: string): Promise<PlaceLocation> {
  if (!legacyAttrDiv) {
    legacyAttrDiv = document.createElement('div');
    legacyAttrDiv.style.display = 'none';
    document.body.appendChild(legacyAttrDiv);
  }
  const service = new google.maps.places.PlacesService(legacyAttrDiv);

  return new Promise((resolve, reject) => {
    service.getDetails(
      {
        placeId,
        fields: ['place_id', 'formatted_address', 'name', 'geometry', 'address_components'],
      },
      (place, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
          reject(new Error(`Place details failed: ${status}`));
          return;
        }
        let locality: string | undefined;
        let country: string | undefined;
        const components = place.address_components?.map((c) => {
          if (c.types.includes('locality')) locality = c.long_name;
          if (c.types.includes('country')) country = c.short_name;
          return { longName: c.long_name, shortName: c.short_name, types: c.types };
        });

        resolve({
          placeId: place.place_id ?? placeId,
          formattedAddress: place.formatted_address ?? '',
          coordinates: {
            lat: place.geometry?.location?.lat() ?? 0,
            lng: place.geometry?.location?.lng() ?? 0,
          },
          name: place.name ?? undefined,
          locality,
          country,
          addressComponents: components,
        });
      },
    );
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GooglePlacesAutocomplete({
  value = '',
  initialValue,
  onInputChange,
  onSelect,
  placeholder = 'Search for a location...',
  className,
  inputClassName,
  apiKey: propApiKey,
  types = DEFAULT_PLACE_TYPES,
  countryCode,
  searchContext,
  showSearchIcon = true,
  showConfigurationErrors = true,
  dropdownClassName,
  autoComplete,
  autoCapitalize,
  enterKeyHint,
}: GooglePlacesAutocompleteProps) {
  const resultsId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const useNewApiRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastQueuedQueryRef = useRef('');
  const activeSearchIdRef = useRef(0);

  const [inputValue, setInputValue] = useState(value || initialValue || '');
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [apiReady, setApiReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [justSelected, setJustSelected] = useState(false);
  const lastInitialValueRef = useRef(initialValue);
  const isFocusedRef = useRef(false);

  const { value: settingsApiKey, isLoading: isLoadingSettingsKey } = useGooglePlacesApiKey({
    enabled: !propApiKey,
  });
  const apiKey = propApiKey || settingsApiKey;
  const isLoadingApiKey = !propApiKey && isLoadingSettingsKey;
  const canSearch = !!apiKey && apiReady && !loadError;

  useEffect(() => {
    if (!apiKey || isLoadingApiKey) return;
    let cancelled = false;

    loadPlacesApi(apiKey)
      .then(() => {
        if (cancelled) return;
        useNewApiRef.current = hasNewApi();
        sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
        setApiReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Failed to load Google Maps API.');
      });

    return () => {
      cancelled = true;
    };
  }, [apiKey, isLoadingApiKey]);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (value) return;
    const prev = lastInitialValueRef.current;
    const next = initialValue || '';
    if (!next) {
      lastInitialValueRef.current = initialValue;
      return;
    }
    if (!inputValue || inputValue === prev) setInputValue(next);
    lastInitialValueRef.current = initialValue;
  }, [initialValue, inputValue, value]);

  const search = useCallback(
    async (query: string, searchId: number) => {
      if (!sessionTokenRef.current || !query.trim()) {
        setPredictions([]);
        return;
      }
      setIsLoading(true);
      try {
        const input = searchContext ? `${query}, ${searchContext}` : query;
        const results = useNewApiRef.current
          ? await fetchSuggestionsNew(
              input,
              sessionTokenRef.current,
              types,
              countryCode ? [countryCode.toLowerCase()] : undefined,
            )
          : await fetchSuggestionsLegacy(
              input,
              sessionTokenRef.current,
              types,
              countryCode?.toLowerCase(),
            );
        if (activeSearchIdRef.current !== searchId) {
          return;
        }
        setPredictions(results);
        setShowResults(results.length > 0);
      } catch {
        if (activeSearchIdRef.current !== searchId) {
          return;
        }
        setPredictions([]);
        setShowResults(false);
      } finally {
        if (activeSearchIdRef.current === searchId) {
          setIsLoading(false);
        }
      }
    },
    [countryCode, searchContext, types],
  );

  useEffect(() => {
    if (!canSearch || justSelected || !isFocusedRef.current) return;

    const normalizedQuery = inputValue.trim();
    if (normalizedQuery.length < 3) {
      lastQueuedQueryRef.current = '';
      activeSearchIdRef.current += 1;
      setPredictions([]);
      setShowResults(false);
      setIsLoading(false);
      return;
    }

    if (normalizedQuery === lastQueuedQueryRef.current) {
      return;
    }

    lastQueuedQueryRef.current = normalizedQuery;
    const searchId = activeSearchIdRef.current + 1;
    activeSearchIdRef.current = searchId;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      void search(normalizedQuery, searchId);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [inputValue, canSearch, search, justSelected]);

  const handleSelect = async (prediction: Prediction) => {
    setJustSelected(true);
    setShowResults(false);
    setInputValue(prediction.mainText);
    setPredictions([]);
    setIsLoading(true);
    lastQueuedQueryRef.current = '';
    activeSearchIdRef.current += 1;

    try {
      const location = useNewApiRef.current
        ? await fetchPlaceDetailsNew(prediction.placeId)
        : await fetchPlaceDetailsLegacy(prediction.placeId);
      onSelect(location);
      sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
    } catch {
      onSelect({
        placeId: prediction.placeId,
        formattedAddress: prediction.fullText,
        coordinates: { lat: 0, lng: 0 },
        name: prediction.mainText,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setInputValue('');
    onInputChange?.('');
    setPredictions([]);
    setShowResults(false);
    setIsLoading(false);
    lastQueuedQueryRef.current = '';
    activeSearchIdRef.current += 1;
    inputRef.current?.focus();
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <div className="relative">
        {showSearchIcon && (
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        )}
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => {
            setJustSelected(false);
            setInputValue(e.target.value);
            onInputChange?.(e.target.value);
          }}
          onFocus={() => {
            isFocusedRef.current = true;
            if (!justSelected && predictions.length > 0) setShowResults(true);
          }}
          onBlur={() => {
            isFocusedRef.current = false;
          }}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showResults}
          aria-controls={showResults ? resultsId : undefined}
          autoComplete={autoComplete}
          autoCapitalize={autoCapitalize}
          enterKeyHint={enterKeyHint}
          spellCheck={false}
          className={cn(showSearchIcon ? 'pl-9 pr-9' : 'pr-9', inputClassName)}
        />
        {inputValue && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
            onClick={handleClear}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
        {(isLoading || isLoadingApiKey) && (
          <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {showConfigurationErrors && !apiKey && (
        <div className="mt-2 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm">
            Google Places API key not configured. Go to Settings → Mapping to add it.
          </span>
        </div>
      )}

      {showConfigurationErrors && loadError && (
        <div className="mt-2 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-800">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm">{loadError}</span>
        </div>
      )}

      {canSearch && showResults && predictions.length > 0 && (
        <div
          id={resultsId}
          className={cn(
            'absolute top-full left-0 right-0 mt-1 bg-white rounded-md shadow-lg border max-h-60 overflow-auto z-50',
            dropdownClassName,
          )}
        >
          {predictions.map((p) => (
            <button
              key={p.placeId}
              type="button"
              className="w-full px-3 py-3 text-left text-sm hover:bg-muted transition-colors flex items-start gap-2"
              onMouseDown={(e) => {
                e.preventDefault();
                void handleSelect(p);
              }}
            >
              <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <div className="font-medium truncate">{p.mainText}</div>
                <div className="text-muted-foreground text-xs truncate">{p.secondaryText}</div>
              </div>
            </button>
          ))}
          <div className="px-3 py-2 text-xs text-muted-foreground border-t bg-muted/30">
            Powered by Google
          </div>
        </div>
      )}
    </div>
  );
}

export default GooglePlacesAutocomplete;
